import alt from "../alt";
import indexBy from "lodash/keyBy";
import AnalyticsUtil from "../utils/AnalyticsUtil";
import AnalyticsActions from "../actions/AnalyticsActions";
import UserStore from "../stores/UserStore";

class AnalyticsStore {
  constructor() {
    this.bindActions(AnalyticsActions);

    this.state = {
      isFetching: false,
      eventBucketOverviews: {},
      eventBucketSales: {},
      eventTicketSales: {},
      passBucketSales: {},
      passBucketsOverview: {}, // indexed by event bucket id
      isFetchingPassBucketsOverview: false,
      attendeeAnalytics: {}, // indexed by event bucket id
      isFetchingAttendeeAnalytics: false,
      geoAnalytics: {}, // indexed by event bucket id
      eventSalesAnalytics: {}, // indexed by event bucket id
      eventSalesAnalyticsLoadingInterval: null,
      eventPassesAnalytics: {}, // indexed by event bucket id
      passSalesAnalytics: {}, // indexed by event bucket id
      isFetchingPassSalesAnalytics: false,
      eventSalesDetailAnalytics: {}, // indexed by event
      salesAnalytics: {}, // indexed by event bucket id
      eventTicketAnalytics: {}, // indexed by event bucket id
      recurringPassAnalytics: {}, // indexed by event bucket id,
      isFetchingRenewalAnalytics: false,
      expiredPassAnalytics: {}, // indexed by event bucket id,
      isFetchingExpiredPassAnalytics: false,
      newSubscriptionAnalytics: {}, // indexed by event bucket id
      isFetchingNewSubscriptionAnalytics: false,
      recurringPassOverview: {}, // indexed by event bucket id,
      isFetchingRecurringPassOverview: false,
    };

    this.exportPublicMethods({
      getEventTicketSalesById: this.getEventTicketSalesById.bind(this),
      getEventBucketSalesById: this.getEventBucketSalesById.bind(this),
      getEventBucketOverview: this.getEventBucketOverview.bind(this),
      getPassBucketSalesById: this.getPassBucketSalesById.bind(this),
      getPassBucketsOverview: this.getPassBucketsOverview.bind(this),
      getAttendeeAnalytics: this.getAttendeeAnalytics.bind(this),
      getAttendeeAnalyticsForVenue: this.getAttendeeAnalyticsForVenue.bind(
        this
      ),
      getAttendeeAnalyticsForTag: this.getAttendeeAnalyticsForTag.bind(this),
      getGeoAnalytics: this.getGeoAnalytics.bind(this),
      getEventSalesAnalytics: this.getEventSalesAnalytics.bind(this),
      getEventSalesDetailAnalytics: this.getEventSalesDetailAnalytics.bind(
        this
      ),
      getEventPassesAnalytics: this.getEventPassesAnalytics.bind(this),
      getPassSalesAnalytics: this.getPassSalesAnalytics.bind(this),
      getSalesAnalytics: this.getSalesAnalytics.bind(this),
      getEventTicketAnalytics: this.getEventTicketAnalytics.bind(this),
      getRecurringPassAnalytics: this.getRecurringPassAnalytics.bind(this),
      getExpiredPassAnalytics: this.getExpiredPassAnalytics.bind(this),
      getNewSubscriptionAnalytics: this.getNewSubscriptionAnalytics.bind(this),
      getRecurringPassOverview: this.getRecurringPassOverview.bind(this),

      getIsFetchingPassBucketsOverview: this.getIsFetchingPassBucketsOverview.bind(this),
      getIsFetchingRenewalAnalytics: this.getIsFetchingRenewalAnalytics.bind(this),
      getIsFetchingExpiredPassAnalytics: this.getIsFetchingExpiredPassAnalytics.bind(this),
      getIsFetchingNewSubscriptionAnalytics: this.getIsFetchingNewSubscriptionAnalytics.bind(this),
      getIsFetchingRecurringPassOverview: this.getIsFetchingRecurringPassOverview.bind(this),
      getIsFetchingAttendeeAnalytics: this.getIsFetchingAttendeeAnalytics.bind(this),
    });
  }

  getEventTicketSalesById(id) {
    return this.state.eventTicketSales[id];
  }

  getEventBucketSalesById(id) {
    return this.state.eventBucketSales[id];
  }

  getEventBucketOverview(id) {
    return this.state.eventBucketOverviews[id];
  }

  getPassBucketSalesById(id) {
    return this.state.passBucketSales[id];
  }

  getPassBucketsOverview(eventBucketId) {
    return this.state.passBucketsOverview[eventBucketId];
  }

  getAttendeeAnalytics(eventBucketId) {
    return this.state.attendeeAnalytics[eventBucketId];
  }

  getAttendeeAnalyticsForVenue(eventBucketId, venueId) {
    return this.state.attendeeAnalytics[`${eventBucketId}-${venueId}`];
  }

  getAttendeeAnalyticsForTag(eventBucketId, tagId) {
    return this.state.attendeeAnalytics[`${eventBucketId}-${tagId}`];
  }

  getGeoAnalytics(eventBucketId) {
    return this.state.geoAnalytics[eventBucketId];
  }

  getEventSalesAnalytics(eventBucketId) {
    return this.state.eventSalesAnalytics[eventBucketId];
  }

  getEventSalesDetailAnalytics(eventId) {
    return this.state.eventSalesDetailAnalytics[eventId];
  }

  getEventPassesAnalytics(eventBucketId) {
    return this.state.eventPassesAnalytics[eventBucketId];
  }

  getPassSalesAnalytics(eventBucketId) {
    return this.state.passSalesAnalytics[eventBucketId];
  }

  getSalesAnalytics(eventBucketId) {
    return this.state.salesAnalytics[eventBucketId];
  }

  getEventTicketAnalytics(eventBucketId) {
    return this.state.eventTicketAnalytics[eventBucketId];
  }

  getRecurringPassAnalytics(eventBucketId) {
    return this.state.recurringPassAnalytics[eventBucketId];
  }

  getExpiredPassAnalytics(eventBucketId) {
    return this.state.expiredPassAnalytics[eventBucketId];
  }

  getNewSubscriptionAnalytics(eventBucketId) {
    return this.state.newSubscriptionAnalytics[eventBucketId];
  }

  getRecurringPassOverview(eventBucketId) {
    return this.state.recurringPassOverview[eventBucketId];
  }

  getIsFetchingPassBucketsOverview() {
    return this.state.isFetchingPassBucketsOverview;
  }

  getIsFetchingRenewalAnalytics() {
    return this.state.isFetchingRenewalAnalytics;
  }

  getIsFetchingExpiredPassAnalytics() {
    return this.state.isFetchingExpiredPassAnalytics;
  }

  getIsFetchingNewSubscriptionAnalytics() {
    return this.state.isFetchingNewSubscriptionAnalytics;
  }

  getIsFetchingRecurringPassOverview() {
    return this.state.isFetchingRecurringPassOverview;
  }

  getIsFetchingAttendeeAnalytics() {
    return this.state.isFetchingAttendeeAnalytics;
  }

  onFetchEventBucketOverview(eventBucketId) {
    AnalyticsUtil.fetchEventBucketOverview(
      UserStore.getState().apiKey,
      eventBucketId
    )
      .then(data => {
        AnalyticsActions.updateEventBucketOverview(eventBucketId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchEventBucketSales(eventBucketId) {
    AnalyticsUtil.fetchEventBucketSales(
      UserStore.getState().apiKey,
      eventBucketId
    )
      .then(data => {
        AnalyticsActions.updateEventBucketSales(eventBucketId, data.sales);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchEventTicketSales(eventId) {
    AnalyticsUtil.fetchEventTicketSales(UserStore.getState().apiKey, eventId)
      .then(data => {
        AnalyticsActions.updateEventTicketSales(eventId, data.sales);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchPassBucketSales(passBucketId) {
    AnalyticsUtil.fetchPassBucketSales(
      UserStore.getState().apiKey,
      passBucketId
    )
      .then(data => {
        AnalyticsActions.updatePassBucketSales(passBucketId, data.sales);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchPassBucketsOverview({ eventBucketId, filterState, startDate, endDate, allTime }) {
    this.setState({ isFetchingPassBucketsOverview: true });
    AnalyticsUtil.fetchPassBucketsOverview(
      UserStore.getState().apiKey,
      eventBucketId,
      filterState,
      startDate,
      endDate,
      allTime,
    )
      .then(data => {
        this.setState({ isFetchingPassBucketsOverview: false });
        AnalyticsActions.updatePassBucketsOverview(eventBucketId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchPassSalesAnalytics({ eventBucketId, isMembership, filterState, startDate, endDate, allTime }) {
    this.setState({ isFetchingPassSalesAnalytics: true });
    AnalyticsUtil.fetchPassSalesAnalytics(
      UserStore.getState().apiKey,
      eventBucketId,
      isMembership,
      filterState,
      startDate,
      endDate,
      allTime,
    )
      .then(data => {
        this.setState({ isFetchingPassSalesAnalytics: false });
        AnalyticsActions.updatePassSalesAnalytics(eventBucketId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchAttendeeAnalytics({ eventBucketId, filterState, startDate, endDate, allTime }) {
    this.setState({ isFetchingAttendeeAnalytics: true });
    AnalyticsUtil.fetchAttendeeAnalytics(
      UserStore.getState().apiKey,
      eventBucketId,
      filterState,
      startDate,
      endDate,
      allTime,
    )
      .then(data => {
        this.setState({ isFetchingAttendeeAnalytics: false });
        AnalyticsActions.updateAttendeeAnalytics(eventBucketId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchAttendeeAnalyticsForVenue({ eventBucketId, venueId }) {
    AnalyticsUtil.fetchAttendeeAnalytics(
      UserStore.getState().apiKey,
      eventBucketId,
      { venue: venueId }
    )
      .then(data => {
        AnalyticsActions.updateAttendeeAnalytics(
          `${eventBucketId}-${venueId}`,
          data
        );
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchAttendeeAnalyticsForTag({ eventBucketId, tagId }) {
    AnalyticsUtil.fetchAttendeeAnalytics(
      UserStore.getState().apiKey,
      eventBucketId,
      { tag: tagId }
    )
      .then(data => {
        AnalyticsActions.updateAttendeeAnalytics(
          `${eventBucketId}-${tagId}`,
          data
        );
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchGeoAnalytics(eventBucketId) {
    AnalyticsUtil.fetchGeoAnalytics(UserStore.getState().apiKey, eventBucketId)
      .then(data => {
        AnalyticsActions.updateGeoAnalytics(eventBucketId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchEventSalesAnalytics({ eventBucketId, startDate, endDate, allTime }) {
    if (this.state.eventSalesAnalyticsLoadingInterval) {
      AnalyticsActions.clearFetchEventSalesAnalyticsInterval();
    }

    const fetch = () =>
      AnalyticsUtil.fetchEventSalesAnalytics(
        UserStore.getState().apiKey,
        eventBucketId,
        startDate,
        endDate,
        allTime
      );

    fetch()
      .then(data => {
        if (!data.loadingCache) {
          AnalyticsActions.updateEventSalesAnalytics(eventBucketId, data);
          return;
        }

        // start watching for the cache to be filled
        const intervalId = setInterval(() => {
          fetch()
            .then(data => {
              // no data yet, keep checking
              if (data.loadingCache) return;

              // cache has been filled and we have the data!
              AnalyticsActions.clearFetchEventSalesAnalyticsInterval();
              AnalyticsActions.updateEventSalesAnalytics(eventBucketId, data);
            })
            .catch(err => {
              AnalyticsActions.clearFetchEventSalesAnalyticsInterval();
              AnalyticsActions.fetchFailed(err);
            });
        }, 1000 * 5); // 5 seconds

        this.setState({ eventSalesAnalyticsLoadingInterval: intervalId });
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onClearFetchEventSalesAnalyticsInterval() {
    if (!this.state.eventSalesAnalyticsLoadingInterval) return;

    clearInterval(this.state.eventSalesAnalyticsLoadingInterval);
    this.setState({
      eventSalesAnalyticsLoadingInterval: null
    });
  }

  onFetchEventSalesDetailAnalytics(eventId) {
    AnalyticsUtil.fetchEventSalesDetailAnalytics(
      UserStore.getState().apiKey,
      eventId
    )
      .then(data => {
        AnalyticsActions.updateEventSalesDetailAnalytics(eventId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchEventPassesAnalytics({ eventBucketId, isMembership, startDate, endDate, allTime }) {
    AnalyticsUtil.fetchEventPassesAnalytics(
      UserStore.getState().apiKey,
      eventBucketId,
      isMembership,
      startDate,
      endDate,
      allTime,
    )
      .then(data => {
        AnalyticsActions.updateEventPassesAnalytics(eventBucketId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchSalesAnalytics(options) {
    this.setState({ isFetching: true });
    AnalyticsUtil.fetchSalesAnalytics(UserStore.getState().apiKey, options)
      .then(data => {
        this.setState({ isFetching: false });
        AnalyticsActions.updateSalesAnalytics(options.event_bucket, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchEventTicketAnalytics({ eventBucketId, filter2State, startDate, endDate, allTime }) {
    this.setState({ isFetching: true });
    AnalyticsUtil.fetchEventTicketAnalytics(
      UserStore.getState().apiKey,
      eventBucketId,
      filter2State,
      startDate,
      endDate,
      allTime,
    )
      .then(data => {
        this.setState({ isFetching: false });
        AnalyticsActions.updateEventTicketAnalytics(eventBucketId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchRecurringPassAnalytics({ eventBucketId, isMembership = false, startDate, endDate, allTime }) {
    this.setState({ isFetchingRenewalAnalytics: true });
    AnalyticsUtil.fetchRecurringPassAnalytics(
      UserStore.getState().apiKey,
      eventBucketId,
      isMembership,
      startDate,
      endDate,
      allTime
    )
      .then(data => {
        this.setState({ isFetchingRenewalAnalytics: false });
        AnalyticsActions.updateRecurringPassAnalytics(eventBucketId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchExpiredPassAnalytics({ eventBucketId, isMembership, startDate, endDate, allTime }) {
    this.setState({ isFetchingExpiredPassAnalytics: true });
    AnalyticsUtil.fetchExpiredPassAnalytics(
      UserStore.getState().apiKey,
      eventBucketId,
      isMembership,
      startDate,
      endDate,
      allTime
    )
      .then(data => {
        this.setState({ isFetchingExpiredPassAnalytics: false });
        AnalyticsActions.updateExpiredPassAnalytics(eventBucketId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchNewSubscriptionAnalytics({ eventBucketId, isMembership, startDate, endDate, allTime }) {
    this.setState({ isFetchingNewSubscriptionAnalytics: true });
    AnalyticsUtil.fetchNewSubscriptionAnalytics(
      UserStore.getState().apiKey,
      eventBucketId,
      isMembership,
      startDate,
      endDate,
      allTime
    )
      .then(data => {
        this.setState({ isFetchingNewSubscriptionAnalytics: false });
        AnalyticsActions.updateNewSubscriptionAnalytics(eventBucketId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onFetchRecurringPassOverview({ eventBucketId, isMembership = false, startDate, endDate, allTime }) {
    this.setState({ isFetchingRecurringPassOverview: true });
    AnalyticsUtil.fetchRecurringPassOverview(
      UserStore.getState().apiKey,
      eventBucketId,
      isMembership,
      startDate,
      endDate,
      allTime
    )
      .then(data => {
        this.setState({ isFetchingRecurringPassOverview: false });
        AnalyticsActions.updateRecurringPassOverview(eventBucketId, data);
      })
      .catch(AnalyticsActions.fetchFailed);
  }

  onUpdateEventTicketSales(data) {
    this.state.eventTicketSales[data.id] = data.saleData;
    this.setState(this.state);
  }

  onUpdateEventBucketOverview(data) {
    this.state.eventBucketOverviews[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateEventBucketSales(data) {
    this.state.eventBucketSales[data.id] = data.saleData;
    this.setState(this.state);
  }

  onUpdatePassBucketSales(data) {
    this.state.passBucketSales[data.id] = data.saleData;
    this.setState(this.state);
  }

  onUpdatePassBucketsOverview(data) {
    this.state.passBucketsOverview[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateAttendeeAnalytics(data) {
    this.state.attendeeAnalytics[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateGeoAnalytics(data) {
    this.state.geoAnalytics[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateEventSalesAnalytics(data) {
    this.state.eventSalesAnalytics[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateEventSalesDetailAnalytics(data) {
    this.state.eventSalesDetailAnalytics[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateEventPassesAnalytics(data) {
    this.state.eventPassesAnalytics[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdatePassSalesAnalytics(data) {
    this.state.passSalesAnalytics[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateSalesAnalytics(data) {
    this.state.salesAnalytics[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateEventTicketAnalytics(data) {
    this.state.eventTicketAnalytics[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateRecurringPassAnalytics(data) {
    this.state.recurringPassAnalytics[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateExpiredPassAnalytics(data) {
    this.state.expiredPassAnalytics[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateNewSubscriptionAnalytics(data) {
    this.state.newSubscriptionAnalytics[data.id] = data.overview;
    this.setState(this.state);
  }

  onUpdateRecurringPassOverview(data) {
    this.state.recurringPassOverview[data.id] = data.overview;
    this.setState(this.state);
  }

  onFetchFailed(e) {
    alert(`Fetching analytics data failed: ${e.message}`);
    throw e;
  }
}

export default alt.createStore(AnalyticsStore, "AnalyticsStore");



// WEBPACK FOOTER //
// ./src/app/stores/AnalyticsStore.js